import { useState } from 'react';
import { useFullScreenHandle } from 'react-full-screen';
import Controls from './components/controls';
import { IOSFullScreen } from './components/ios-full-screen/ios-full-screen';
import MainLoader from './components/main-loader';
import Resource from './components/resource';
import SingleResource from './components/single-resource';
import Timeline from './components/timeline';
import usePlayerSize from './hooks/use-player-size';
import PlayerProvider from './providers/player-providers/player-provider';
import usePlayerState from './providers/player-providers/use-player-state';
import VideoProvider from './providers/video-providers/video-provider';
import {
  Container,
  ContainerParent,
  FullScreenWrapper,
  NoTimeLineSeparator,
  StyledFullScreen,
  TimelineContainer,
  Wrapper,
} from './resource-viewer.styles';
import { ResourceViewerProps } from './types/resource-viewer';
import { isTouchDevice } from './utils/helpers';

function App() {
  const {
    fullscreen,
    totalSteps,
    resource,
    isSinglePdfOrGenially,
    fullscreenIOS,
  } = usePlayerState();
  const [hideTimeline, setHideTimeline] = useState(false);
  const [observerAdded, setObserverAdded] = useState(false);
  const [objectObserved, setObjectObserved] = useState<Element>();

  const showTimeline = totalSteps > 1;

  const isInFullscreen = !!fullscreen?.active;
  const { showLoader } = usePlayerSize();
  const isTouchable = isTouchDevice();

  // When is only a PDF or Genially this component no need fullscreen function
  if (resource && isSinglePdfOrGenially) {
    return <SingleResource />;
  }

  const observer = new MutationObserver(function (mutations) {
    mutations.forEach(function () {
      const keyboard = document.getElementsByClassName('TabbedKeyBoard');
      const attributes = keyboard.length > 0 ? keyboard[0].attributes : {};
      setHideTimeline(!(attributes as Record<string, boolean>)['aria-hidden']);
    });
  });
  const config = { attributes: true, childList: true, characterData: true };

  const updateTimeline = () => {
    const keyboard = document.getElementsByClassName('TabbedKeyBoard');
    const target = keyboard.length > 0 ? keyboard[0] : undefined;
    if (isTouchable && observerAdded && target && target !== objectObserved) {
      observer.disconnect();

      const attributes = keyboard.length > 0 ? keyboard[0].attributes : {};
      setHideTimeline(!(attributes as Record<string, boolean>)['aria-hidden']);

      observer.observe(target, config);
      setObjectObserved(target);
    } else if (isTouchable && !observerAdded && keyboard.length > 0) {
      const attributes = keyboard.length > 0 ? keyboard[0].attributes : {};
      setHideTimeline(!(attributes as Record<string, boolean>)['aria-hidden']);

      if (target) {
        observer.observe(target, config);
        setObjectObserved(target);
      }

      setObserverAdded(true);
    }
  };

  return (
    <IOSFullScreen isInIOSFullscreen={fullscreenIOS}>
      <Wrapper isInFullscreen={isInFullscreen || fullscreenIOS}>
        <ContainerParent>
          <Container id="resource-wrapper" onFocus={updateTimeline}>
            {showLoader ? <MainLoader /> : <Resource />}
          </Container>
        </ContainerParent>
        <TimelineContainer isInFullscreen={isInFullscreen || fullscreenIOS}>
          {showTimeline && !hideTimeline ? (
            <Timeline />
          ) : (
            <NoTimeLineSeparator />
          )}
          {!hideTimeline && <Controls />}
        </TimelineContainer>
      </Wrapper>
    </IOSFullScreen>
  );
}

export function ResourceViewer({
  resource,
  onDispatchEvent,
  translations,
  appletInfo,
  preventArrowKeysEvents,
  fullScreen,
  apolloServer,
  axiosInstance,
  setEventData,
  videoOptions,
  school,
  uid,
  autoplay,
}: ResourceViewerProps) {
  const handleFS = useFullScreenHandle();
  const fullscreen = fullScreen || handleFS;

  return (
    <FullScreenWrapper data-testid="fullscreenWrapper">
      <StyledFullScreen handle={fullscreen}>
        <PlayerProvider
          resource={resource}
          onDispatchEvent={onDispatchEvent}
          translations={translations}
          appletInfo={appletInfo}
          fullscreen={fullscreen}
          preventArrowKeysEvents={preventArrowKeysEvents}
          apolloServer={apolloServer}
          axiosInstance={axiosInstance}
          setEventData={setEventData}
          videoOptions={videoOptions}
          school={school}
          uid={uid}
          autoplay={autoplay}
        >
          <VideoProvider>
            <App />
          </VideoProvider>
        </PlayerProvider>
      </StyledFullScreen>
    </FullScreenWrapper>
  );
}
